/** The Parts > Bids tabs */
export type PartProBidsTab = "open" | "closed" | "completed" | "rejected";

/** REJECTED: the yard rejected the whole car after bidding - every accepted bid was cancelled */
export type PartProTradeVehicleStatus = "DRAFT" | "OPEN" | "CLOSED" | "COMPLETED" | "WITHDRAWN" | "REJECTED";
/** CANCELLED: accepted, then the yard cancelled the award - final, never re-awarded */
export type PartProTradeLotStatus = "PENDING" | "AWARDED" | "NOT_AWARDED" | "CANCELLED";

/** How PartPro spells a cancellation reason on an award; VEHICLE_REJECTED when the whole car was rejected */
export type PartProTradeAwardCancelReasonCode = "DAMAGED" | "MISSING" | "INACCESSIBLE" | "OTHER" | "VEHICLE_REJECTED";

/** How PartPro spells why a whole vehicle was rejected */
export type PartProTradeRejectReasonCode = "NOT_ARRIVED" | "NOT_AS_DESCRIBED" | "OTHER";

// Mirrors backend PartProTradeCancelReason - sent as its number
export enum PartProTradeCancelReason {
  Damaged = 0,
  Missing = 1,
  Inaccessible = 2,
  Other = 3,
}

// Mirrors backend PartProTradeRejectReason - sent as its number
export enum PartProTradeRejectReason {
  NotArrived = 0,
  NotAsDescribed = 1,
  Other = 2,
}

export interface IPartProTradeBidVehicle {
  /** PartPro trade vehicle id */
  id: string;
  /** The Core vehicle it was pushed from, when there is one */
  coreVehicleId: string | null;
  status: PartProTradeVehicleStatus;
  vrm: string | null;
  name: string;
  photo: string | null;
  biddingEndsAt: string | null;
  closedAt: string | null;
  rejectedAt: string | null;
  rejectReason: PartProTradeRejectReasonCode | null;
  rejectNote: string | null;
  lotCount: number;
  lotsWithBids: number;
  activeBidCount: number;
  /** Sum of each category's highest bid, pounds */
  highestBidTotal: number;
  pendingLotCount: number;
  awardedLotCount: number;
  awardedTotal: number;
}

export interface IPartProTradeBidsPage {
  vehicles: IPartProTradeBidVehicle[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  openCount: number;
  closedCount: number;
  completedCount: number;
  rejectedCount: number;
}

export interface IPartProTradeBid {
  id: string;
  amount: number;
  message: string | null;
  amountSetAt: string;
  /** PartPro's pre-selected winner once bidding closed */
  isAutoPick: boolean;
  buyerCompanyName: string;
  buyerEmail: string | null;
  buyerPhone: string | null;
  /** The Core account the buyer is linked to, if any */
  accountId: string | null;
  accountName: string | null;
}

export interface IPartProTradeBidAward {
  bidId: string;
  amount: number;
  commissionPct: number;
  commission: number;
  overridden: boolean;
  overrideReason: string | null;
  awardedAt: string;
  /** Set once the award was cancelled; the lot is then CANCELLED */
  cancelledAt: string | null;
  cancelReason: PartProTradeAwardCancelReasonCode | null;
  cancelNote: string | null;
  buyerCompanyName: string;
  accountId: string | null;
  accountName: string | null;
}

export interface IPartProTradeBidLot {
  id: string;
  status: PartProTradeLotStatus;
  categoryName: string;
  partCategoryId: string | null;
  /** Highest first; the earliest committed amount wins ties */
  bids: IPartProTradeBid[];
  award: IPartProTradeBidAward | null;
}

export interface IPartProTradeBidVehicleDetail {
  id: string;
  coreVehicleId: string | null;
  status: PartProTradeVehicleStatus;
  vrm: string | null;
  name: string;
  photos: string[];
  biddingEndsAt: string | null;
  closedAt: string | null;
  commissionPct: number | null;
  rejectedAt: string | null;
  rejectReason: PartProTradeRejectReasonCode | null;
  rejectNote: string | null;
  lots: IPartProTradeBidLot[];
}

/** One pick in a batch accept - at most one per category */
export interface IAcceptPartProTradeBid {
  lotId: string;
  bidId: string;
  overrideReason?: string;
}

/** Cancel an accepted bid - the buyer is emailed the reason and the matrix cell is cleared */
export interface ICancelPartProTradeAward {
  reason: PartProTradeCancelReason;
  note?: string;
}

/** Reject the whole vehicle - every accepted bid is cancelled and the car stays off Trade until rescheduled */
export interface IRejectPartProTradeVehicle {
  reason: PartProTradeRejectReason;
  note?: string;
}

export interface IDecidePartProTradeLot {
  /** null = don't award this category */
  bidId: string | null;
  overrideReason?: string;
}
